import { PlayerRef } from '@remotion/player';
import { DropPosition } from '../assets/add-asset';
import { EditorStarterAsset } from '../assets/assets';
import { cacheAssetLocally } from '../caching/indexeddb';
import { setLocalUrl } from '../caching/load-to-blob-url';
import { getSvgDimensions } from '../utils/get-svg-dimensions';
import { makeAudioItem } from './audio/make-audio-item';
import { makeGifItem } from './gif/make-gif-item';
import { makeImageItem } from './image/make-image-item';
import { EditorStarterItem } from './item-type';
import { makeVideoItem } from './video/make-video-item-wkwk';
import { makeWebpItem } from './webp/make-webp-item';
import { MIN_VOLUME_DB } from '../utils/decibels';
import { DtoProjectType } from '@/shared/constants';
import { CLIP_TYPES, ContentClipInfo } from '@/shared/apis/editor/clip-types';
import {
  FILE_METADATA_ERROR_MESSAGES,
  formatVideoMetadataLoadError,
} from '@/shared/utils/file';
import { releaseVideoElement } from '@/shared/utils/video-element-cleanup';

const GIF_MIME_TYPE = 'image/gif';
const WEBP_MIME_TYPE = 'image/webp';
const WEBP_ANIMATION_FLAG = 0b00000010;

const normalizeExtension = (filename: string) => {
  const rawExtension = filename.split('.').pop();
  if (!rawExtension) {
    return null;
  }

  const normalized = rawExtension.trim().toLowerCase().replace(/^\./, '');
  const withoutQuery = normalized.split('?')[0] ?? '';
  const withoutHash = withoutQuery.split('#')[0] ?? '';
  return withoutHash || null;
};

const readFourCC = (view: DataView, offset: number): string => {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );
};

const isAnimatedWebp = async (file: Blob): Promise<boolean> => {
  const buffer = await file.arrayBuffer();

  if (buffer.byteLength < 16) {
    return false;
  }

  const view = new DataView(buffer);
  const riff = readFourCC(view, 0);
  const webp = readFourCC(view, 8);

  if (riff !== 'RIFF' || webp !== 'WEBP') {
    return false;
  }

  let offset = 12;
  while (offset + 8 <= view.byteLength) {
    const chunkId = readFourCC(view, offset);
    const chunkSize = view.getUint32(offset + 4, true);
    const chunkDataOffset = offset + 8;

    if (chunkDataOffset > view.byteLength) {
      break;
    }

    if (chunkId === 'ANIM' || chunkId === 'ANMF') {
      return true;
    }

    if (
      chunkId === 'VP8X' &&
      chunkSize >= 1 &&
      chunkDataOffset + 1 <= view.byteLength
    ) {
      const flags = view.getUint8(chunkDataOffset);
      if ((flags & WEBP_ANIMATION_FLAG) !== 0) {
        return true;
      }
    }

    if (chunkDataOffset + chunkSize > view.byteLength) {
      break;
    }

    const paddedChunkSize = chunkSize + (chunkSize % 2);
    offset = chunkDataOffset + paddedChunkSize;
  }

  return false;
};

const isGifFile = ({
  file,
  filename,
}: {
  file: Blob;
  filename: string;
}): boolean => {
  const mimeType = file.type.toLowerCase();
  const extension = normalizeExtension(filename);
  return mimeType === GIF_MIME_TYPE || extension === 'gif';
};

const isAnimatedWebpFile = async ({
  file,
  filename,
}: {
  file: Blob;
  filename: string;
}): Promise<boolean> => {
  const mimeType = file.type.toLowerCase();
  const extension = normalizeExtension(filename);
  const isWebp = mimeType === WEBP_MIME_TYPE || extension === 'webp';
  if (!isWebp) {
    return false;
  }
  return isAnimatedWebp(file);
};

export const makeContentItem = async ({
  file,
  assetId,
  clip,
  playerRef,
  dropPosition,
  fps,
  compositionWidth,
  compositionHeight,
  filename,
  remoteUrl,
  remoteFileKey,
  projectType,
  timelineStartFrame,
  durationInSeconds,
}: {
  file: Blob;
  assetId: string;
  clip: ContentClipInfo;
  playerRef: React.RefObject<PlayerRef | null>;
  dropPosition: DropPosition | null;
  fps: number;
  compositionWidth: number;
  compositionHeight: number;
  remoteUrl: string | null;
  remoteFileKey: string | null;
  filename: string;
  projectType: DtoProjectType;
  /** 타임라인에서 아이템이 시작될 프레임 (지정 시 clip.videoStart 대신 사용) */
  timelineStartFrame?: number;
  /** 아이템 duration 고정값 (선택, 이미지/GIF에서 사용) */
  durationInSeconds?: number;
}): Promise<{ item: EditorStarterItem; asset: EditorStarterAsset }> => {
  const url = URL.createObjectURL(file);
  setLocalUrl(assetId, url);

  await cacheAssetLocally({
    assetId: assetId,
    value: file,
  });

  // 상위에서 image로 들어와도 실제 파일이 GIF이면 GIF 아이템으로,
  // 애니메이션 WebP이면 WebP 아이템으로 보정
  const baseType = clip?.clipType ?? CLIP_TYPES.VIDEO;
  let type = baseType;
  if (baseType === CLIP_TYPES.IMAGE) {
    if (isGifFile({ file, filename })) {
      type = CLIP_TYPES.GIF;
    } else if (await isAnimatedWebpFile({ file, filename })) {
      type = CLIP_TYPES.WEBP;
    }
  }

  if (type === CLIP_TYPES.AUDIO) {
    // const durationInSeconds = await getAudioDurationInSeconds(url);

    const startSeconds = (clip.videoStart ?? 0) / 1000;
    const endSeconds = (clip.videoEnd ?? startSeconds) / 1000;
    const startFrame = Math.round(startSeconds * fps);
    const endFrame = Math.round(endSeconds * fps);
    const durationFrames = Math.max(1, endFrame - startFrame);
    // timelineStartFrame이 지정되면 사용, 아니면 기존 로직 유지
    const currentFrame = timelineStartFrame ?? startFrame;
    const durationInSeconds = durationFrames / fps;

    // console.log(
    //   `audio filename: ${filename}, linkId: ${clip.group_id}, currentFrame: ${currentFrame}, audioStartFromInSeconds: 0, durationInSeconds: ${durationInSeconds}`,
    // );

    const { item, asset } = makeAudioItem({
      file,
      fps,
      durationInSeconds: durationInSeconds,
      currentFrame: currentFrame,
      size: file.size,
      assetId,
      filename,
      remoteUrl,
      remoteFileKey,
    });
    return { item, asset };
  }

  if (type === CLIP_TYPES.VIDEO) {
    const videoMeta = await getVideoMetadata(url);

    const startSeconds = (clip.videoStart ?? 0) / 1000;
    const endSeconds = (clip.videoEnd ?? startSeconds) / 1000;
    const startFrame = Math.round(startSeconds * fps);
    const endFrame = Math.round(endSeconds * fps);
    const durationFrames = Math.max(1, endFrame - startFrame);
    // timelineStartFrame이 지정되면 사용, 아니면 기존 로직 유지
    const currentFrame = timelineStartFrame ?? startFrame;
    const durationInSeconds = durationFrames / fps;
    const videoStartFromInSeconds = (clip.start ?? 0) / 1000;

    // console.log(
    //   `video filename: ${filename}, linkId: ${clip.group_id}, currentFrame: ${currentFrame}, videoStartFromInSeconds: ${videoStartFromInSeconds}, durationInSeconds: ${durationInSeconds}`,
    // );

    // projectType에 따른 decibelAdjustment 결정
    // highlight, spotlight 프로젝트는 음소거, 나머지는 기본 볼륨
    const decibelAdjustment =
      projectType === DtoProjectType.commerce ? MIN_VOLUME_DB : 0;

    const { item, asset } = makeVideoItem({
      file,
      fps,
      compositionWidth,
      compositionHeight,
      currentFrame: currentFrame,
      durationInSeconds: durationInSeconds,
      videoStartFromInSeconds: videoStartFromInSeconds,
      sourceDurationInSeconds: videoMeta.durationInSeconds,
      dimensions: { width: videoMeta.width, height: videoMeta.height },
      dropPosition,
      assetId,
      hasAudioTrack: true,
      filename,
      remoteUrl,
      remoteFileKey,
      decibelAdjustment,
    });
    return { item, asset };
  }

  if (type === CLIP_TYPES.GIF) {
    const { width, height } = await getImageDimensions(url);
    const { item, asset } = await makeGifItem({
      file,
      fps,
      compositionWidth,
      compositionHeight,
      currentFrame:
        timelineStartFrame ?? playerRef.current?.getCurrentFrame() ?? 0,
      dropPosition,
      dimensions: { width, height },
      assetId,
      blobUrl: url,
      filename,
      remoteUrl,
      remoteFileKey,
      durationInSeconds,
    });
    return { item, asset };
  }

  if (type === CLIP_TYPES.WEBP) {
    const { width, height } = await getImageDimensions(url);
    const { item, asset } = await makeWebpItem({
      file,
      fps,
      compositionWidth,
      compositionHeight,
      currentFrame:
        timelineStartFrame ?? playerRef.current?.getCurrentFrame() ?? 0,
      dropPosition,
      dimensions: { width, height },
      assetId,
      filename,
      remoteUrl,
      remoteFileKey,
      durationInSeconds,
    });
    return { item, asset };
  }

  if (type === CLIP_TYPES.IMAGE) {
    if (file.type === 'image/svg+xml') {
      const svgText = await file.text();
      const dimensions = getSvgDimensions(svgText);
      const { item, asset } = await makeImageItem({
        file,
        fps,
        compositionWidth,
        compositionHeight,
        currentFrame:
          timelineStartFrame ?? playerRef.current?.getCurrentFrame() ?? 0,
        dropPosition,
        dimensions,
        assetId,
        filename,
        remoteUrl,
        remoteFileKey,
        durationInSeconds,
      });
      return { item, asset };
    }

    const { width, height } = await getImageDimensions(url);
    const { item, asset } = await makeImageItem({
      file,
      fps,
      compositionWidth,
      compositionHeight,
      currentFrame:
        timelineStartFrame ?? playerRef.current?.getCurrentFrame() ?? 0,
      dropPosition,
      assetId,
      filename,
      remoteUrl,
      remoteFileKey,
      dimensions: { width, height },
      durationInSeconds,
    });
    return { item, asset };
  }

  throw new Error('Unknown asset type');
};

// Helpers
const getImageDimensions = (
  blobUrl: string,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Could not load image'));
    img.src = blobUrl;
  });
};

const getAudioDurationInSeconds = (blobUrl: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    const cleanup = () => {
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.removeAttribute('src');
      audio.load();
    };
    audio.onloadedmetadata = () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      cleanup();
      resolve(duration);
    };
    audio.onerror = () => {
      cleanup();
      reject(new Error(FILE_METADATA_ERROR_MESSAGES.AUDIO_LOAD_FAILED));
    };
    audio.src = blobUrl;
  });
};

const getVideoMetadata = (
  blobUrl: string,
): Promise<{
  durationInSeconds: number;
  width: number;
  height: number;
  hasAudioTrack: boolean;
}> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    // blobUrl 의 revokeObjectURL 은 호출자 책임으로 둔다 (인자 생략).
    const cleanup = () => releaseVideoElement(video);
    video.onloadedmetadata = () => {
      const durationInSeconds = Number.isFinite(video.duration)
        ? video.duration
        : 0;
      const width = video.videoWidth;
      const height = video.videoHeight;
      let hasAudioTrack = false;
      try {
        if (
          (video as any).audioTracks &&
          typeof (video as any).audioTracks.length === 'number'
        ) {
          hasAudioTrack = (video as any).audioTracks.length > 0;
        }
        if (!hasAudioTrack && typeof (video as any).mozHasAudio === 'boolean') {
          hasAudioTrack = (video as any).mozHasAudio as boolean;
        }
      } catch {}
      cleanup();
      resolve({ durationInSeconds, width, height, hasAudioTrack });
    };
    video.onerror = () => {
      const code = video.error?.code;
      const message = video.error?.message;
      cleanup();
      reject(new Error(formatVideoMetadataLoadError(code, message)));
    };
    video.src = blobUrl;
  });
};
